import { Refresh } from "@mui/icons-material";
import { ClickAwayListener, Fade, Paper, Popper } from "@mui/material";
import { FC, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
  AccessControl,
  RebuildRouteDropdownAppliedInfo,
  RebuildRouteDropdownCustomRebuildHeading,
  RebuildRouteDropdownMenu,
  RebuildRouteDropdownSubmitButton,
  RebuildRouteProviderSelector,
} from "@ryde/controls";
import {
  formatRebuildRouteNoCacheButtonCopy,
  formatRebuildRouteTooltip,
  IStationRouteDto,
  RebuildRouteTypeEnum,
  resolveRebuildRouteProvider,
} from "@ryde/shared";

import {
  StationRouteRebuildRouteDropdownDefaultAction,
  StationRouteRebuildRouteDropdownOptionsSelector,
  StationRouteRebuildRouteDropdownTopButton,
} from "./parts";
import { StationRouteRebuildRouteDropdownWrapper } from "./station-route-rebuild-route-dropdown.styled";
import { useStationRouteRebuildRouteDropdown } from "./use-station-route-rebuild-route-dropdown";

interface IStationRouteRebuildRouteDropdownProps {
  stationRouteDto: Partial<IStationRouteDto> | undefined;
}

export const StationRouteRebuildRouteDropdown: FC<
  IStationRouteRebuildRouteDropdownProps
> = ({ stationRouteDto }) => {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLButtonElement>(null);

  const {
    open,
    isLoading,
    systemDefaultProvider,
    lastUsedProvider,
    selectedProvider,
    setSelectedProvider,
    avoidTolls,
    setAvoidTolls,
    skipCache,
    setSkipCache,
    routeOption,
    setRouteOption,
    supportsAvoidTolls,
    systemDefaultAvoidTolls,
    hasCustomRebuildType,
    lastSelectedRebuildType,
    lastRouteRebuildAt,
    handleDropdownToggle,
    handleClose: handleCloseFromHook,
    handleDefaultRebuild,
    handleRebuild,
  } = useStationRouteRebuildRouteDropdown(stationRouteDto);

  const rebuildRouteTooltip = useMemo(
    () =>
      formatRebuildRouteTooltip({
        baseLabel: t("general.REBUILD_SAVE_CACHE"),
        lastUsedProvider,
        systemDefaultProvider,
      }),
    [lastUsedProvider, systemDefaultProvider, t],
  );

  const rerouteProvider = resolveRebuildRouteProvider({
    selectedProvider,
    lastUsedProvider,
    systemDefaultProvider,
  });
  const rerouteRebuildType =
    routeOption || RebuildRouteTypeEnum.ProviderOptimization;

  const noCacheButtonCopy = useMemo(
    () =>
      formatRebuildRouteNoCacheButtonCopy({
        provider: rerouteProvider,
        rebuildType: rerouteRebuildType,
        t,
      }),
    [rerouteProvider, rerouteRebuildType, t],
  );

  const handleClose = (event: Event | React.SyntheticEvent): void => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    handleCloseFromHook(event);
  };

  return (
    <AccessControl entity="stationRoute" action="update">
      <StationRouteRebuildRouteDropdownWrapper $isOpen={open}>
        <StationRouteRebuildRouteDropdownTopButton
          anchorRef={anchorRef}
          isLoading={isLoading}
          hasCustomRebuildType={hasCustomRebuildType}
          rebuildRouteTooltip={rebuildRouteTooltip}
          onDefaultRebuild={() => {
            void handleDefaultRebuild();
          }}
          onDropdownToggle={handleDropdownToggle}
        />

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          transition
          disablePortal
          placement="bottom-end"
          style={{ zIndex: 1300 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper elevation={8}>
                <ClickAwayListener onClickAway={handleClose}>
                  <RebuildRouteDropdownMenu>
                    <RebuildRouteDropdownAppliedInfo
                      lastUsedProvider={lastUsedProvider}
                      systemDefaultProvider={systemDefaultProvider}
                      routeRebuildAt={stationRouteDto?.metadata?.routeRebuildAt}
                      sessionRouteRebuildAt={lastRouteRebuildAt}
                      lastSelectedRebuildType={lastSelectedRebuildType}
                      onClose={handleClose}
                    />

                    <StationRouteRebuildRouteDropdownDefaultAction
                      skipCache={skipCache}
                      isLoading={isLoading}
                      lastUsedProvider={lastUsedProvider}
                      systemDefaultProvider={systemDefaultProvider}
                      onSkipCacheChange={setSkipCache}
                      onRebuild={handleDefaultRebuild}
                    />

                    <RebuildRouteDropdownCustomRebuildHeading />

                    <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--providers">
                      <RebuildRouteProviderSelector
                        selectedProvider={selectedProvider}
                        systemDefaultProvider={systemDefaultProvider}
                        lastUsedProvider={lastUsedProvider}
                        onProviderChange={setSelectedProvider}
                      />
                    </div>

                    <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--options">
                      <StationRouteRebuildRouteDropdownOptionsSelector
                        supportsAvoidTolls={supportsAvoidTolls}
                        avoidTolls={avoidTolls}
                        isDefaultAvoidTolls={systemDefaultAvoidTolls}
                        routeOption={routeOption}
                        selectedProvider={rerouteProvider}
                        onAvoidTollsChange={setAvoidTolls}
                        onRouteOptionChange={setRouteOption}
                      />

                      <RebuildRouteDropdownSubmitButton
                        label={noCacheButtonCopy.title}
                        hint={noCacheButtonCopy.hint}
                        tooltip={t("general.RE_ROUTE_NO_CACHE_TOOLTIP")}
                        startIcon={<Refresh />}
                        isLoading={isLoading}
                        onClick={handleRebuild}
                      />
                    </div>
                  </RebuildRouteDropdownMenu>
                </ClickAwayListener>
              </Paper>
            </Fade>
          )}
        </Popper>
      </StationRouteRebuildRouteDropdownWrapper>
    </AccessControl>
  );
};
